import { useNavigate } from 'react-router-dom';
import { Users, Sparkles, LayoutGrid, Armchair, Plus, HelpCircle, UtensilsCrossed } from 'lucide-react';

import {
  useServiceTables,
  useOrders,
  useUpdateServiceTable,
  deriveTabState,
  resolveServeLabel,
  type ServiceTable,
} from '@/lib/api';
import { formatNPR } from '@/components/Money';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { LoadingState } from '@/components/LoadingState';
import { RefreshButton } from '@/components/RefreshButton';
import { IconGlyph } from '@/components/IconPicker';
import { PageShell } from '@/components/PageShell';
import { timeAgo } from '@/lib/dates';
import { bucketOpenOrders } from '@/lib/floor';
import { toast } from '@/lib/toast';
import { usePermissions } from '@/lib/permissions';

export function FloorPage() {
  const tables = useServiceTables();
  const orders = useOrders('open');
  const updateTable = useUpdateServiceTable();
  const nav = useNavigate();
  const { can } = usePermissions();
  const canOpenTab = can('order:create'); // open a new tab on a table / walk-in
  const canSweep = can('table:update'); // mark a dirty table clean
  const canStaffMeal = canOpenTab;

  // Split the open orders into the three things the floor actually shows. A
  // staff meal has no table (0076 forbids it one), so bucketing on
  // service_table_id alone used to drop it into the walk-in grid where it
  // rendered as an ordinary paying guest. See lib/floor.ts.
  const { byTable: openByTable, walkins, staffMeals } = bucketOpenOrders(orders.data ?? []);

  // Open a draft tab with no table — for a customer who orders before deciding
  // where to sit. No order row is created yet; the first item added persists it.
  // It can be assigned to (or merged into) a table later from the tab's Move
  // action.
  const onUnknown = () => {
    nav('/admin/floor/new');
  };

  // Staff meals are one shared, running tab — what the team ate, never who
  // ate it (0085). Rejoin it if it is open; otherwise start a draft, which the
  // first item persists. It never counts as a sale, so it is started here
  // rather than being rung up on a table and discounted to zero afterwards.
  const onStaffMeal = () => {
    const running = staffMeals[0];
    if (running) {
      nav(`/admin/floor/${running.id}`);
      return;
    }
    nav('/admin/floor/new', { state: { staffMeal: true } });
  };

  const onClickTable = (t: ServiceTable) => {
    const existing = openByTable.get(t.id);
    if (existing) {
      nav(`/admin/floor/${existing.id}`);
      return;
    }
    if (t.status === 'dirty') {
      // Block opening a tab on a dirty table — mark it clean first via
      // the per-tile sweep button. Mirrors what a host would do IRL.
      return;
    }
    // Open a draft tab — nothing is persisted and the table stays free until
    // the first item is added (which creates the order and flips it occupied).
    nav('/admin/floor/new', { state: { tableId: t.id, tableName: t.name } });
  };

  const onSweep = async (t: ServiceTable) => {
    try {
      await updateTable.mutateAsync({ id: t.id, patch: { status: 'free' } });
      toast.success(`${t.name} cleaned`, 'ready for the next guest');
    } catch (e: unknown) {
      toast.error('Could not mark clean', (e as { message?: string }).message);
    }
  };

  return (
    <PageShell
      eyebrow="Operations"
      title="Floor"
      actions={
        <>
          <span className="meta-line">
            {orders.data?.length ?? 0} open · {tables.data?.length ?? 0} tables
          </span>
          {/* A staff meal is a rare, deliberate act — a handful a day against
              hundreds of serves. It used to sit in the walk-in grid as a tile
              the same size and shape as "Unknown +", competing for the thumb
              with the thing staff reach for constantly. Demoted to the header,
              where it is available without being in the way. */}
          {canStaffMeal && (
            <button
              type="button"
              className="btn"
              onClick={onStaffMeal}
              title="Free food for the team — recorded at cost, never a sale"
            >
              <UtensilsCrossed size={14} strokeWidth={1.5} /> Staff meal
            </button>
          )}
          <RefreshButton
            onClick={() => Promise.all([tables.refetch(), orders.refetch()])}
            busy={tables.isFetching || orders.isFetching}
            label="Refresh floor"
          />
        </>
      }
    >
      {tables.isPending && <LoadingState />}
      {tables.isError && !tables.data && <ErrorState onRetry={() => tables.refetch()} />}
      {tables.data?.length === 0 && (
        <EmptyState
          icon={<LayoutGrid size={40} strokeWidth={1.4} style={{ color: 'var(--amber-fg)' }} />}
          title="No tables yet"
          emoji="🪑"
          hint={
            <>
              Set up your floor in <strong>Admin · Tables</strong> — the
              tabs you open will appear here.
            </>
          }
        />
      )}

      <div className="floor-grid" data-tour="floor-tables">
        {(tables.data ?? []).map((t) => {
          const order = openByTable.get(t.id);
          const occupied = !!order || t.status === 'occupied';
          const isDirty = !occupied && t.status === 'dirty';
          return (
            <div
              key={t.id}
              className={`floor-tile-wrap ${isDirty ? 'dirty' : ''}`}
            >
              <button
                type="button"
                className={`floor-tile ${occupied ? 'occupied' : t.status}`}
                onClick={() => onClickTable(t)}
                // A tile with an existing order is navigable (order:read). One
                // without would open a new tab, so disable it for members who
                // lack order:create.
                disabled={isDirty || (!order && !canOpenTab)}
              >
                <div className="ft-head">
                  <span className="ft-name">
                    <span className="ft-icon" aria-hidden>
                      <IconGlyph name={t.icon} size={16} fallback={<Armchair size={16} strokeWidth={1.5} />} />
                    </span>
                    <span className="ft-name__text" title={t.name}>{t.name}</span>
                  </span>
                  <span className="ft-cap" aria-label={`Seats ${t.capacity}`} title={`Seats ${t.capacity}`}>
                    <Users size={12} strokeWidth={1.5} aria-hidden="true" /> {t.capacity}
                  </span>
                </div>
                <div className="ft-body">
                  {order ? (
                    <>
                      <div className="ft-amt">{formatNPR(order.live_subtotal_cents)}</div>
                      <div className="ft-meta">
                        {order.items_total} items · {timeAgo(order.opened_at)}
                      </div>
                      {(() => {
                        const s = deriveTabState(order);
                        if (!s) return null;
                        return (
                          <div className={`ft-state ft-state--${s.tone}`} title={s.hint}>
                            {s.label}
                          </div>
                        );
                      })()}
                    </>
                  ) : (
                    <div className="ft-cta">
                      {t.status === 'free'
                        ? 'Open tab'
                        : t.status === 'occupied'
                        ? 'Occupied'
                        : t.status === 'reserved'
                        ? 'Reserved'
                        : 'Dirty'}
                    </div>
                  )}
                </div>
                {t.area && <div className="ft-area">{t.area}</div>}
              </button>
              {isDirty && canSweep && (
                <button
                  type="button"
                  className="ft-sweep"
                  onClick={() => onSweep(t)}
                  disabled={updateTable.isPending}
                  title="Mark clean → free"
                >
                  <Sparkles size={12} strokeWidth={1.5} />
                  Mark clean
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Table-less serves. The "Takeaway" tile opens one; existing ones link to
       * their tab, where the serve can be named, switched to delivery, or
       * assigned to (merged into) a table. */}
      <div className="floor-section">
        <div className="floor-section-head">Takeaway &amp; walk-ins</div>
        <div className="floor-grid">
          {walkins.map((o) => {
            const s = deriveTabState(o);
            return (
              <button
                key={o.id}
                type="button"
                className="floor-tile occupied"
                onClick={() => nav(`/admin/floor/${o.id}`)}
              >
                <div className="ft-head">
                  <span className="ft-name">
                    <span className="ft-icon" aria-hidden>
                      <HelpCircle size={16} strokeWidth={1.5} />
                    </span>
                    <span className="ft-name__text" title={resolveServeLabel(o)}>
                      {resolveServeLabel(o)}
                    </span>
                  </span>
                </div>
                <div className="ft-body">
                  <div className="ft-amt">{formatNPR(o.live_subtotal_cents)}</div>
                  <div className="ft-meta">
                    {o.items_total} items · {timeAgo(o.opened_at)}
                  </div>
                  {s && (
                    <div className={`ft-state ft-state--${s.tone}`} title={s.hint}>
                      {s.label}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
          {canOpenTab && (
            <button
              type="button"
              className="floor-tile unknown-add"
              onClick={onUnknown}
            >
              <span className="ua-plus" aria-hidden>
                <Plus size={20} strokeWidth={1.6} />
              </span>
              <span className="ua-label">Takeaway</span>
              <span className="ua-sub">a serve with no table</span>
            </button>
          )}
        </div>
      </div>

      {/* Open staff meals, on their own and plainly labelled. These used to be
       * indistinguishable from walk-ins: same tile, same amber amount, titled
       * "Walk-in". A tile that looks like a serve but will never be paid for is
       * exactly the thing the floor should not be quiet about. Normally there
       * is one; a cafe that had several open under the old per-person model
       * still sees each until it is finished. */}
      {staffMeals.length > 0 && (
        <div className="floor-section floor-section--staff">
          <div className="floor-section-head">Staff meals · not sales</div>
          <div className="floor-grid">
            {staffMeals.map((o) => (
              <button
                key={o.id}
                type="button"
                className="floor-tile occupied staff-meal"
                onClick={() => nav(`/admin/floor/${o.id}`)}
              >
                <div className="ft-head">
                  <span className="ft-name">
                    <span className="ft-icon" aria-hidden>
                      <UtensilsCrossed size={16} strokeWidth={1.5} />
                    </span>
                    <span className="ft-name__text" title={o.table_label || 'Staff meals'}>
                      {o.table_label || 'Staff meals'}
                    </span>
                  </span>
                </div>
                <div className="ft-body">
                  <div className="ft-amt">{formatNPR(o.live_subtotal_cents)}</div>
                  <div className="ft-meta">
                    {o.items_total} items · {timeAgo(o.opened_at)}
                  </div>
                  <div className="ft-tag">staff meal · at cost</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </PageShell>
  );
}
